
import { useState, useEffect, useCallback, useRef } from "react";
import { addFollower } from "../components/functions/addFollower";
import {
  loadCityData,
  getCitySuggestions,
} from "../components/functions/localCityAutocomplete";
import { useTranslation } from "../i18n";
import { HookUTMParams } from "./HookUTMParams";


const applyDateMask = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

const applyPhoneMask = (value) => {
  const trimmed = value.trim();
  const hasPlus = trimmed.startsWith("+");
  const digits = value.replace(/\D/g, "");
  const limited = digits.slice(0, 15);


  if (limited.length === 0) return hasPlus ? "+" : "";


  if (hasPlus) {

    if (limited.startsWith("55") && limited.length > 2) {
      const cc = limited.slice(0, 2);
      const ddd = limited.slice(2, 4);
      const part1 = limited.slice(4, 9);
      const part2 = limited.slice(9, 13);
      if (limited.length <= 4) return `+${cc} ${ddd}`;
      if (limited.length <= 9) return `+${cc} ${ddd} ${part1}`;
      return `+${cc} ${ddd} ${part1}-${part2}`;
    }
    return `+${limited}`;
  }


  if (limited.length <= 2) return `(${limited}`;
  if (limited.length <= 7) return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
  return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7, 11)}`;
};


const applyCpfMask = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6)
    return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};


const isValidCpf = (cpf) => {
  const digits = cpf.replace(/\D/g, "");
  if (digits.length !== 11) return false;

  if (/^(\d)\1{10}$/.test(digits)) return false;


  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
  let remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[9])) return false;


  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[10])) return false;

  return true;
};

// Valida dd/mm/aaaa: dia/mês existem de fato (fev 30 é rejeitado) e a data
// não está no futuro nem é implausivelmente antiga.
const isValidBirthDate = (value) => {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return false;
  const [, dd, mm, yyyy] = match;
  const day = parseInt(dd, 10);
  const month = parseInt(mm, 10);
  const year = parseInt(yyyy, 10);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return false;
  }
  const now = new Date();
  if (date > now) return false;
  if (year < 1900) return false;
  return true;
};

export const HookNewsletterForm = () => {
  const { t, lang } = useTranslation();
  const utmParams = HookUTMParams();

  const [showForm, setShowForm] = useState(false);


  const [preferredContact, setPreferredContact] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");
  const [fullName, setFullName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [cityState, setCityState] = useState("");
  const [instagram, setInstagram] = useState("");
  const [nationality, setNationality] = useState("");
  // null = no choice made yet (required Yes/No, no default per business rule)
  const [attendedEvent, setAttendedEvent] = useState(null);


  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);


  const [allCities, setAllCities] = useState([]);
  const [citySuggestions, setCitySuggestions] = useState([]);
  // Rastreia se o valor atual veio de um clique numa sugestão, para não
  // recalcular/reabrir a lista quando o nome escolhido é prefixo de outra
  // cidade do CSV (ex.: "New York" vs "New York Mills").
  const justSelectedCityRef = useRef(false);


  // Default por idioma ao abrir o formulário: site em PT pré-seleciona Brasil.
  // Só preenche valor vazio — nunca sobrescreve escolha do usuário.
  useEffect(() => {
    if (showForm && lang === "pt") {
      setNationality((prev) => prev || "BR");
    }
  }, [showForm, lang]);


  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const cities = await loadCityData();
        if (!cancelled) {
          setAllCities(cities);
        }
      } catch (err) {
        if (!cancelled) {
          console.error(
            "[HookNewsletterForm] failed to load city CSV:",
            err
          );
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);


useEffect(() => {
  if (justSelectedCityRef.current) {
    justSelectedCityRef.current = false;
    return;
  }

  const query = cityState.trim();

  if (query.length < 2 || allCities.length === 0) {
    setCitySuggestions([]);
    return;
  }

  const suggestions = getCitySuggestions(query, allCities, 10);
  setCitySuggestions(suggestions);
}, [cityState, allCities]);



  const onPreferredContactChange = (e) => {
    setPreferredContact(e.target.value);
  };

  const onEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const onPhoneChange = (e) => {
    const masked = applyPhoneMask(e.target.value);
    setPhone(masked);
  };

  const onCpfChange = (e) => {
    const masked = applyCpfMask(e.target.value);
    setCpf(masked);
  };

  const onFullNameChange = (e) => {
    setFullName(e.target.value);
  };

  const onBirthDateChange = (e) => {
    const masked = applyDateMask(e.target.value);
    setBirthDate(masked);
  };

  const onCityStateChange = (e) => {
    setCityState(e.target.value);
  };

  const onCitySuggestionSelect = (label) => {
    justSelectedCityRef.current = true;
    setCityState(label);
    setCitySuggestions([]);
  };

  const onNationalityChange = (e) => {
    const value = e.target.value;
    setNationality(value);
    // Saiu do Brasil → CPF não se aplica mais; evita CPF fantasma no estado
    if (value !== "BR") setCpf("");
  };


  const onInstagramChange = (e) => {
    let val = e.target.value.replace(/\s/g, "");

    if (val && !val.startsWith("@")) val = "@" + val;

    val = val.replace(/[^@a-zA-Z0-9_.]/g, "");
    setInstagram(val);
  };


  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitError(null);


      if (!fullName.trim()) {
        setSubmitError(t("validation.nameRequired"));
        return { ok: false };
      }
      if (!nationality) {
        setSubmitError(t("validation.nationalityRequired"));
        return { ok: false };
      }
      if (!birthDate.trim()) {
        setSubmitError(t("validation.birthRequired"));
        return { ok: false };
      }
      if (!isValidBirthDate(birthDate)) {
        setSubmitError(t("validation.birthInvalid"));
        return { ok: false };
      }
      if (nationality === "BR") {
        if (!cpf.trim()) {
          setSubmitError(t("validation.cpfRequired"));
          return { ok: false };
        }
        if (!isValidCpf(cpf)) {
          setSubmitError(t("validation.cpfInvalid"));
          return { ok: false };
        }
      }
      if (!cityState.trim()) {
        setSubmitError(t("validation.cityRequired"));
        return { ok: false };
      }
      if (!preferredContact) {
        setSubmitError(t("validation.contactRequired"));
        return { ok: false };
      }

      // E-mail e telefone agora são sempre obrigatórios, independente do
      // canal de contato preferido escolhido.
      if (!email.trim()) {
        setSubmitError(t("validation.emailRequired"));
        return { ok: false };
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        setSubmitError(t("validation.emailInvalid"));
        return { ok: false };
      }
      const phoneDigits = phone.replace(/\D/g, "");
      if (!phone.trim()) {
        setSubmitError(t("validation.phoneRequired"));
        return { ok: false };
      }
      if (phoneDigits.length < 8) {
        setSubmitError(t("validation.phoneInvalid"));
        return { ok: false };
      }
      if (preferredContact === "Instagram DM") {
        const clean = instagram.replace("@", "").trim();
        if (clean.length < 2) {
          setSubmitError(t("validation.instagramInvalid"));
          return { ok: false };
        }
      }
      if (attendedEvent === null) {
        setSubmitError(t("validation.attendedEventRequired"));
        return { ok: false };
      }

      const followerData = {
        preferredContact,
        email,
        phone,
        cpf: nationality === "BR" ? cpf : "",
        fullName,
        instagram,
        birthDate,
        cityState,
        nationality,
        attendedEvent,
        utmParams,
      };

      try {
        setIsSubmitting(true);
        const result = await addFollower(followerData);
        return result;
      } catch (err) {
        console.error("[HookNewsletterForm] submit error:", err);
        setSubmitError(t("validation.genericError"));
        return { ok: false };
      } finally {
        setIsSubmitting(false);
      }
    },
    [preferredContact, email, phone, cpf, fullName, instagram, birthDate, cityState, attendedEvent, nationality, t, utmParams]
  );
  

  return {
    showForm,
    setShowForm,

    preferredContact,
    email,
    phone,
    cpf,
    fullName,
    birthDate,
    cityState,
    instagram,
    nationality,
    attendedEvent,
    setAttendedEvent,

    onPreferredContactChange,
    onEmailChange,
    onPhoneChange,
    onCpfChange,
    onFullNameChange,
    onBirthDateChange,
    onCityStateChange,
    onCitySuggestionSelect,
    onInstagramChange,
    onNationalityChange,


    citySuggestions,


    isSubmitting,
    submitError,
    setSubmitError,

    onSubmit,
  };
};

export default HookNewsletterForm;
